<template>
  <div>
    <div class="flex flex-wrap lg-section ti-page-start pt2 pb1">
      <div class="sm-col md-col-5 px3 pb2">
        <h1 class="ti-h2">Vendor Profiles</h1>
        <div class="ti-under"></div>
      </div>
      <div class="sm-col md-col-6 px3">
        <p class="ti-page-intro">
          Select Technologies and Sectors to view vendors that are active in those areas.
          <span :title="info" style="color:rgba(0,0,0,0.6);font-size:14px;">
            🛈
          </span>
        </p>
      </div>
    </div>

    <div v-if="!loading" class="lg-section flex px2">
      <div class="sm-col sm-col-12 md-col-5 pb3">
        <FilterTerm @updateTerms="updateTerms" :initTerms="$store.state.terms" />
        <TreeFilter allLabel="All ISIC Verticals" headerClass="ti-tag-sector"
                    :listItems="$store.state.refMap.vertical_items"
                    :items="$store.state.refMap.verticals"
                    :filters="$store.state.verticals" :counts="counts.verticals"
                    @itemClick="toggleVerticalItem"
                    class="mt2"/>
        <TreeFilter allLabel="All Technologies" headerClass="ti-tag-technology"
                    :listItems="$store.state.refMap.tech_items"
                    :groups="$store.state.refMap.tech_areas"
                    :items="$store.state.refMap.technologies"
                    :filters="$store.state.techs"
                    :countsGroup="counts.techareas" :counts="counts.techs"
                    @groupClick="toggleTechGroup" @itemClick="toggleTechItem"
                    :defs="TechDef"
                    class="mt2"/>
      </div>

      <div class="sm-col sm-col-12 md-col-6 ml2"
           style="position: sticky; top:10px; align-self: flex-start;">
        <div v-if="refreshing">...</div>
        <template v-else-if="vendors && vendors.length">
          <div v-for="vendor in vendors" :key="vendor.id"
               class="flex ti-row-highlight" style="padding: 6px 8px;">
            <input type="checkbox" :checked="$store.state.compare[vendor.id]"
                   :disabled="!vendor.accessible"
                   @change="toggleVendor(vendor.id, vendor.name)"/>
            <BarGauge :size="3" :val="vendor.rate_max-1" class="px2"/>
            <router-link :to="{name: 'vendor-detail',
                              params: {id: vendor.id, slug:toSlug(vendor.name)}}"
                         class="flex-auto ti-link ti-link-underline"
                         style="color:black;">
              {{vendor.name}}
            </router-link>
            <router-link v-if="vendor.connect" class="ti-link"
                         :to="{name: 'connect-detail',
                              params: {id: vendor.id, slug:toSlug(vendor.name)}}">
              Vendor Connect ➜
            </router-link>
          </div>
          <div class="mt2 right-align">
            <Paginate :totalPages="$store.state.page.totalPages"
                      :currentPage="$store.state.page.currentPage"
                      @goPage="goPage">
            </Paginate>
          </div>
        </template>
        <div v-else class="ti-p">
          &nbsp;No Matches
        </div>
        <div class="mt2">
          <router-link :to="{name: 'vendor-compare', query: {vendors: compVendors}}"
                       tag="input" class="btn btn-primary"
                       :disabled="!canAccess || compVendors.length === 0"
                       value="Compare Selected Vendors"
                       style="width:300px;"/>
          <div v-if="!canAccess" class="ti-note pl1 pt1">
            A subscription of <strong>ESSENTIAL</strong> or better is required to compare Vendors.
          </div>
          <ul class="my1">
            <li v-for="(vendorName, vendorId) in $store.state.compare"
                :key="vendorId" class="text-tag">
              {{vendorName}}<span class="text-tag-close"
                                  @click="toggleVendor(vendorId, '')"> ❌</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

import TechDef from '@/tech';
import BarGauge from '@/components/BarGauge.vue';
import Paginate from '@/components/Paginate.vue';
import FilterTerm from '@/components/FilterTerm.vue';
import TreeFilter from '@/components/TreeFilter.vue';

export default {
  name: 'VendorList',
  components: {
    BarGauge, Paginate, FilterTerm, TreeFilter,
  },
  data() {
    return {
      TechDef,
      loading: true, // first load of refMap
      // refMap dicts for creating filters

      refreshing: true, // refreshing list of studies and counts
      canAccess: null,
      vendors: null,
      counts: {
        verticals: {},
        techareas: {},
        techs: {},
      },

      info: `The order of the vendor list is based on the count of ‘Market Leading’ capability ratings within the scope of the configured search,
with second order ranking based on the number of ‘Significant’ ratings,
and third order based on the number of ‘Emerging’ ratings.

The bars represent the maximum capability of a vendor within the scope of the configured search:
 - 3 bars for Market Leading capability;
 - 2 bars for Significant;
 - 1 for Emerging;
 - 0 for no or negligible capability.
`,
    };
  },

  computed: {
    compVendors() {
      return Object.keys(this.$store.state.compare);
    },
  },

  created() {
    if (this.$store.state.refMap === null) {
      axios.get('/api/vendors/schema')
        .then((res) => {
          this.$store.commit('initMaps', res.data.refMap);
          this.loading = false;
        });
    } else {
      this.loading = false;
    }
    const pageIndex = this.$store.state.page.currentPage || 1;
    this.getVendors(pageIndex, 'load');
  },

  methods: {
    /* tc: track control, which action an user performed */
    getVendors(page = 1, tc = null) {
      // get list of studies
      this.refreshing = true;
      const postData = {
        page,
        filters: {
          verticals: this.$store.state.verticals,
          techs: this.$store.state.techs,
        },
        terms: this.$store.state.terms,
      };
      const params = { tc };
      if (this.$store.state.terms) {
        params.q = [...this.$store.state.terms];
        if (page) {
          params.page = page;
        }
      }

      this.$axios.post('/api/vendors', postData, { params })
        .then((res) => {
          this.canAccess = res.data.canAccess;
          this.vendors = res.data.vendors;
          this.$store.commit('setPage', res.data.page);
          this.counts = res.data.counts;
          this.refreshing = false;
        });
    },

    goPage(pageIndex) {
      this.getVendors(pageIndex, 'page');
    },

    toggleVerticalItem(param) {
      let payload;
      if (param === null) {
        payload = { property: 'verticals', val: null };
      } else {
        const current = Boolean(this.$store.state.verticals[param]);
        payload = { property: 'verticals', id: param, val: !current };
      }
      this.$store.commit('setFilter', payload);
      this.$nextTick().then(() => this.getVendors(1, 'filter'));
    },

    toggleTechGroup(groupCode, val) {
      this.$store.commit('setTechGroup', { groupCode, val });
      this.$nextTick().then(() => this.getVendors(1, 'filter'));
    },
    toggleTechItem(param) {
      let payload;
      if (param === null) {
        payload = { property: 'techs', val: null };
      } else {
        const current = Boolean(this.$store.state.techs[param]);
        payload = { property: 'techs', id: param, val: !current };
      }
      this.$store.commit('setFilter', payload);
      this.$nextTick().then(() => this.getVendors(1, 'filter'));
    },

    updateTerms(terms) {
      this.$store.commit('setTerms', terms);
      this.$nextTick()
        .then(() => {
          this.getVendors(1, 'term');
        });
    },

    toggleVendor(vendorId, vendorName) {
      const payload = { id: vendorId, val: vendorName };
      this.$store.commit('toggleCompare', payload);
    },
  },
};
</script>

<style>
  .ti-row-highlight:hover {
    background: #DFE1E5;
    opacity: 0.7;
  }
</style>
